import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ListChecks, Plus, Trash2, Users, X, Search, Loader2, Circle, CheckCircle2, LayoutGrid, Rows3, Settings, ChevronUp, ChevronDown, GripVertical, ArrowUpDown, ArrowUp, ArrowDown, ArrowRightLeft, History } from 'lucide-react';
import { todoApi } from '../../lib/api';

// Lot 2 — écran du ToDo (fondations serveur : Lot 1, voir functions/api/todo.js et
// REPRISE-TODO-NOTES.md à la racine du projet). Le contenu partagé (useTodoBoard +
// TodoBoardContent) sert aussi bien à cet écran plein qu'au widget flottant
// (FloatingTodoWidget.tsx) — une seule logique, deux habillages.
//
// Simplifications assumées pour ce premier écran (voir aussi les commentaires de todo.js) :
//   - pas encore de rattachement à un rendez-vous de prestation depuis cet écran (la colonne
//     existe côté serveur, mais aucun geste ne la remplit pour l'instant).

export type TodoSpace = { id: string; name: string; user_id: string; mine: boolean; partage_actif?: number };
export type TodoList = { id: string; space_id: string; name: string; color: string | null };
export type TodoTask = {
    id: string; list_id: string; name: string | null; done: number; done_at: string | null;
    criticality_id: string | null; due_date: string | null; alert_mode: string | null; alert_date: string | null;
    created_at?: string;
};
export type Criticality = { id: string; name: string; color: string };
type Collaborator = { id: string; name: string; username: string; email?: string };
// A3 : une ligne d'historique -- suppression (annuler = deleted_at redevient null) ou
// déplacement de tâche (annuler = rejoue la position de départ, conservée côté serveur).
type ActionLogEntry = {
    id: string; space_id: string; action_type: 'delete_space' | 'delete_list' | 'delete_task' | 'move_task';
    entity_id: string; label: string; created_at: string;
};
type Toast = { id: string; message: string; actionLabel?: string; onAction?: () => void };

const fmtDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
/** Identifiant purement local (toasts) -- jamais envoyé au serveur, pas besoin d'UUID. */
const generateClientId = () => Math.random().toString(36).slice(2);

/** A3 : "il y a 3 min" / "il y a 2 h" / date courte au-delà d'un jour -- pour l'écran d'historique. */
const fmtRelativeTime = (iso: string) => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `il y a ${h} h`;
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ---------- Échéances et alertes (D1-D11) ----------
// `alert_mode` : null (aucune) | 'veille' | 'semaine_avant' | 'on_date'.

const ALERT_MODE_LABELS: { key: string; label: string }[] = [
    { key: '', label: 'Aucune' }, { key: 'veille', label: 'La veille' },
    { key: 'semaine_avant', label: 'Une semaine avant' }, { key: 'on_date', label: 'À une date précise' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso: string, days: number) => {
    const d = new Date(iso + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};
/** D3 : la date d'alerte des modes relatifs se calcule à la lecture, jamais stockée. */
const alertDateFor = (task: TodoTask): string | null => {
    if (!task.due_date || !task.alert_mode) return null;
    if (task.alert_mode === 'veille') return addDays(task.due_date, -1);
    if (task.alert_mode === 'semaine_avant') return addDays(task.due_date, -7);
    if (task.alert_mode === 'on_date') return task.alert_date;
    return null;
};
/** D6 : active dès que la date d'alerte est atteinte ou dépassée, tant que la tâche n'est pas faite. */
const isAlertActive = (task: TodoTask): boolean => {
    if (task.done) return false;
    const ad = alertDateFor(task);
    return !!ad && ad <= todayISO();
};

/** Accents et casse indifférents dans la recherche (G2). */
const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const TODO_VIEW_MODE_KEY = 'iamani-todo-view-mode';

// ---------- Tri à la demande (F1-F5) ----------
// F2 : ne range QUE l'affichage — jamais écrit en base, l'ordre manuel (position) reste intact
// dessous. F3 : propre à chaque liste et à chaque poste (localStorage, pas envoyé au serveur).

type SortBy = 'alpha' | 'due' | 'crit' | 'created';
type SortState = { by: SortBy; dir: 'asc' | 'desc' } | null;
const SORT_LABELS: { by: SortBy; label: string }[] = [
    { by: 'alpha', label: 'Alphabétique' }, { by: 'due', label: 'Échéance' },
    { by: 'crit', label: 'Criticité' }, { by: 'created', label: "Ordre d'ajout" },
];
const TODO_SORT_KEY_PREFIX = 'iamani-todo-sort-';
const readListSort = (listId: string): SortState => {
    try {
        const raw = localStorage.getItem(TODO_SORT_KEY_PREFIX + listId);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

/** F5 : les trous (pas de date, pas de niveau) restent en bas dans les deux sens — jamais en tête
 *  d'un tri décroissant. */
const sortTasks = (tasks: TodoTask[], sort: SortState, criticalityOrder: Map<string, number>): TodoTask[] => {
    if (!sort) return tasks;
    const mult = sort.dir === 'asc' ? 1 : -1;
    return [...tasks].sort((a, b) => {
        if (sort.by === 'alpha') {
            const an = a.name, bn = b.name;
            if (!an && !bn) return 0;
            if (!an) return 1;
            if (!bn) return -1;
            return mult * an.localeCompare(bn, 'fr');
        }
        if (sort.by === 'due') {
            if (!a.due_date && !b.due_date) return 0;
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return mult * a.due_date.localeCompare(b.due_date);
        }
        if (sort.by === 'crit') {
            const ao = a.criticality_id ? criticalityOrder.get(a.criticality_id) : undefined;
            const bo = b.criticality_id ? criticalityOrder.get(b.criticality_id) : undefined;
            if (ao === undefined && bo === undefined) return 0;
            if (ao === undefined) return 1;
            if (bo === undefined) return -1;
            return mult * (ao - bo);
        }
        // created : toujours croissant/décroissant par date de création, jamais de trou possible.
        const ac = a.created_at || '';
        const bc = b.created_at || '';
        return mult * ac.localeCompare(bc);
    });
};
type TodoStatusFilter = 'tout' | 'a_faire' | 'faites';

/** Pastille de couleur d'un tableau dérivée de son id (stable même après renommage) — B2. */
const SPACE_DOT_COLORS = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#0ea5e9', '#8b5cf6', '#ec4899', '#14b8a6'];
const colorForSpaceId = (id: string) => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    return SPACE_DOT_COLORS[hash % SPACE_DOT_COLORS.length];
};

/** Six teintes très pâles + « Aucune couleur » pour le fond d'une liste — B11. Des rgba à faible
 *  opacité plutôt que des tons pastel fixes : ça reste discret aussi bien en thème sombre qu'en
 *  thème clair, sans avoir à définir deux palettes. */
const LIST_TINTS: { name: string; value: string | null }[] = [
    { name: 'Aucune couleur', value: null },
    { name: 'Rouge', value: 'rgba(239,68,68,0.10)' },
    { name: 'Orange', value: 'rgba(245,158,11,0.10)' },
    { name: 'Jaune', value: 'rgba(234,179,8,0.10)' },
    { name: 'Vert', value: 'rgba(34,197,94,0.10)' },
    { name: 'Bleu', value: 'rgba(14,165,233,0.10)' },
    { name: 'Violet', value: 'rgba(139,92,246,0.10)' },
];

/** A3 : pas de composant toast partagé dans ce projet (chaque écran a le sien) -- on reprend le
 *  même style local que le reste de l'app plutôt que d'introduire un système global pour un seul
 *  module. Empilé en bas à droite ; chaque toast disparaît de lui-même après ~8s. */
function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
    if (toasts.length === 0) return null;
    return (
        <div className="fixed bottom-4 right-4 z-[80] flex flex-col gap-2 items-end">
            <AnimatePresence>
                {toasts.map((t) => (
                    <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                        className="bg-surface border border-border rounded-xl shadow-xl px-4 py-3 flex items-center gap-3 text-sm text-content max-w-sm">
                        <span className="flex-1">{t.message}</span>
                        {t.actionLabel && t.onAction && (
                            <button onClick={() => { t.onAction!(); onDismiss(t.id); }}
                                className="font-bold text-[var(--brand-color)] hover:underline shrink-0">{t.actionLabel}</button>
                        )}
                        <button onClick={() => onDismiss(t.id)} className="text-content-muted hover:text-content shrink-0"><X size={14} /></button>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}

const ACTION_LABELS: Record<ActionLogEntry['action_type'], string> = {
    delete_space: 'Tableau supprimé', delete_list: 'Liste supprimée', delete_task: 'Tâche supprimée', move_task: 'Tâche déplacée',
};

/** A3 : historique des actions annulables du tableau ouvert, restaurable individuellement --
 *  survit à un rechargement puisqu'il vient du serveur (todo_action_log), pas d'une pile en mémoire. */
function HistoryDialog({ actions, loading, onRestore, onClose }: {
    actions: ActionLogEntry[]; loading: boolean; onRestore: (id: string) => void; onClose: () => void;
}) {
    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content flex items-center gap-2"><History size={18} /> Historique</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-1.5 max-h-[60vh] overflow-y-auto">
                        {loading ? (
                            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
                        ) : actions.length === 0 ? (
                            <p className="text-xs text-content-muted px-1 py-4 text-center">Rien à restaurer pour l'instant.</p>
                        ) : (
                            actions.map((a) => (
                                <div key={a.id} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg hover:bg-surface-soft">
                                    <span className="min-w-0">
                                        <span className="text-content block truncate">{ACTION_LABELS[a.action_type]} — « {a.label} »</span>
                                        <span className="text-[11px] text-content-muted">{fmtRelativeTime(a.created_at)}</span>
                                    </span>
                                    <button onClick={() => onRestore(a.id)} className="font-bold text-[var(--brand-color)] hover:underline shrink-0 ml-2 text-xs">Restaurer</button>
                                </div>
                            ))
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

function ShareDialog({ type, id, name, onClose }: { type: 'space' | 'list'; id: string; name: string; onClose: () => void }) {
    const [shares, setShares] = React.useState<Collaborator[]>([]);
    const [candidates, setCandidates] = React.useState<Collaborator[] | null>(null);
    const [owner, setOwner] = React.useState<Collaborator | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [picked, setPicked] = React.useState('');

    const getShares = type === 'space' ? todoApi.getSpaceShares : todoApi.getListShares;
    const doShare = type === 'space' ? todoApi.shareSpace : todoApi.shareList;
    const doUnshare = type === 'space' ? todoApi.unshareSpace : todoApi.unshareList;

    const load = React.useCallback(() => {
        setLoading(true);
        getShares(id)
            .then((r: any) => { setShares(r.shares || []); setCandidates(r.candidates); setOwner(r.owner); })
            .finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        if (!picked) return;
        await doShare(id, picked);
        setPicked('');
        load();
    };
    const handleRemove = async (userId: string, personName: string) => {
        // H7 : pas annulable — la confirmation est le seul filet, elle dit ce qui va se passer.
        if (!window.confirm(`Retirer l'accès de ${personName} à « ${name} » ? Cette personne ne pourra plus la voir ni la modifier.`)) return;
        await doUnshare(id, userId);
        load();
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content flex items-center gap-2"><Users size={18} /> Partage de « {name} »</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
                        ) : (
                            <>
                                <div className="space-y-1.5">
                                    {/* H5 : ordre alphabétique côté serveur, propriétaire toujours en tête ici,
                                        adresse sous le nom pour départager deux homonymes au moment de retirer. */}
                                    {owner && (
                                        <div className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg bg-surface-soft">
                                            <span className="min-w-0">
                                                <span className="text-content font-medium block truncate">{owner.name || owner.username}</span>
                                                {(owner.email || owner.username) && <span className="text-[11px] text-content-muted block truncate">{owner.email || owner.username}</span>}
                                            </span>
                                            <span className="text-[10px] font-bold uppercase text-content-muted shrink-0 ml-2">Propriétaire</span>
                                        </div>
                                    )}
                                    {shares.map((s) => (
                                        <div key={s.id} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg">
                                            <span className="min-w-0">
                                                <span className="text-content block truncate">{s.name || s.username}</span>
                                                {(s.email || s.username) && <span className="text-[11px] text-content-muted block truncate">{s.email || s.username}</span>}
                                            </span>
                                            {/* H4 : le bouton retirer n'existe QUE pour le propriétaire (candidates non nul) —
                                                pas grisé, absent, pour tous les autres qui ouvrent cet écran en lecture seule. */}
                                            {candidates !== null && (
                                                <button onClick={() => handleRemove(s.id, s.name || s.username)} className="text-content-muted hover:text-red-500 shrink-0 ml-2"><X size={14} /></button>
                                            )}
                                        </div>
                                    ))}
                                    {shares.length === 0 && <p className="text-xs text-content-muted px-3">Pas encore partagé.</p>}
                                </div>
                                {candidates !== null && (
                                    <div className="flex gap-2 pt-2 border-t border-border">
                                        <select value={picked} onChange={(e) => setPicked(e.target.value)}
                                            className="flex-1 bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-sm text-content">
                                            <option value="">Choisir un collègue…</option>
                                            {candidates.map((c) => <option key={c.id} value={c.id}>{c.name || c.username}</option>)}
                                        </select>
                                        <button onClick={handleAdd} disabled={!picked}
                                            className="px-3 py-2 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold disabled:opacity-40">
                                            Partager
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

function PromptDialog({ title, placeholder, confirmLabel = 'Créer', onSubmit, onClose }: {
    title: string; placeholder: string; confirmLabel?: string; onSubmit: (value: string) => void; onClose: () => void;
}) {
    const [value, setValue] = React.useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => { inputRef.current?.focus(); }, []);

    const submit = () => {
        const trimmed = value.trim();
        if (!trimmed) return;
        onSubmit(trimmed);
        onClose();
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content">{title}</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-4">
                        <input
                            ref={inputRef}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }}
                            placeholder={placeholder}
                            className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2.5 text-sm text-content placeholder:text-content-muted outline-none focus:border-[var(--brand-color)]"
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm font-bold text-content-muted hover:text-content">Annuler</button>
                            <button onClick={submit} disabled={!value.trim()}
                                className="px-4 py-2 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold disabled:opacity-40">
                                {confirmLabel}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

/** Le rond de criticité EST le bouton qui ouvre le choix du niveau (comme dans le paquet
 *  d'origine) : un `<select>` habillé en pastille colorée, plutôt qu'une flèche voisine à
 *  viser séparément. "—" = pas de niveau, jamais retenu comme une vraie valeur. */
function CriticalityPicker({ value, levels, onChange }: { value: string | null; levels: Criticality[]; onChange: (id: string | null) => void }) {
    const current = value ? levels.find((l) => l.id === value) : null;
    return (
        <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            title="Criticité"
            className="text-[10px] font-bold px-1.5 py-0.5 rounded-full outline-none cursor-pointer bg-transparent"
            style={current ? { color: current.color, border: `1px solid ${current.color}55` } : { color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
        >
            <option value="">—</option>
            {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
    );
}

/** Écran de réglages des niveaux de criticité (C3) : renommer, recolorer, supprimer, réordonner —
 *  partagés par tout l'établissement, pas par tableau. Réordonner se fait par flèches haut/bas
 *  plutôt qu'un glisser-déposer : pas d'infrastructure de drag-and-drop dans ce module pour
 *  l'instant (voir la tâche E1-E7 séparée sur le réordonnancement des tâches). */
function CriticalitySettingsDialog({ levels, onCreate, onUpdate, onDelete, onReorder, onClose }: {
    levels: Criticality[];
    onCreate: (name: string, color: string) => void;
    onUpdate: (id: string, payload: { name?: string; color?: string }) => void;
    onDelete: (id: string) => void;
    onReorder: (order: string[]) => void;
    onClose: () => void;
}) {
    const [newName, setNewName] = React.useState('');
    const [newColor, setNewColor] = React.useState('#38bdf8');

    const move = (index: number, dir: -1 | 1) => {
        const target = index + dir;
        if (target < 0 || target >= levels.length) return;
        const order = levels.map((l) => l.id);
        [order[index], order[target]] = [order[target], order[index]];
        onReorder(order);
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content flex items-center gap-2"><Settings size={18} /> Niveaux de criticité</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-4">
                        <div className="space-y-1.5">
                            {levels.map((l, i) => (
                                <div key={l.id} className="flex items-center gap-2">
                                    <div className="flex flex-col shrink-0">
                                        <button onClick={() => move(i, -1)} disabled={i === 0} className="text-content-muted hover:text-content disabled:opacity-20"><ChevronUp size={13} /></button>
                                        <button onClick={() => move(i, 1)} disabled={i === levels.length - 1} className="text-content-muted hover:text-content disabled:opacity-20"><ChevronDown size={13} /></button>
                                    </div>
                                    <input type="color" value={l.color} onChange={(e) => onUpdate(l.id, { color: e.target.value })}
                                        className="w-7 h-7 rounded-lg border border-border shrink-0 cursor-pointer bg-transparent" title="Couleur" />
                                    <input defaultValue={l.name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== l.name) onUpdate(l.id, { name: v }); else e.target.value = l.name; }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                        className="flex-1 min-w-0 bg-surface-soft border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-[var(--brand-color)]" />
                                    <button onClick={() => onDelete(l.id)} className="text-content-muted hover:text-red-500 shrink-0"><Trash2 size={14} /></button>
                                </div>
                            ))}
                            {levels.length === 0 && <p className="text-xs text-content-muted">Aucun niveau pour l'instant.</p>}
                        </div>
                        <div className="flex items-center gap-2 pt-2 border-t border-border">
                            <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
                                className="w-7 h-7 rounded-lg border border-border shrink-0 cursor-pointer bg-transparent" title="Couleur" />
                            <input value={newName} onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) { onCreate(newName.trim(), newColor); setNewName(''); } }}
                                placeholder="Nouveau niveau…"
                                className="flex-1 min-w-0 bg-surface-soft border border-dashed border-border rounded-lg px-2.5 py-1.5 text-sm text-content placeholder:text-content-muted outline-none focus:border-[var(--brand-color)]" />
                            <button onClick={() => { if (newName.trim()) { onCreate(newName.trim(), newColor); setNewName(''); } }} disabled={!newName.trim()}
                                className="px-3 py-1.5 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold disabled:opacity-40 shrink-0">
                                Ajouter
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

/** D1/D2/D4 : poser une échéance et son mode d'alerte. Le panneau grandit avec le besoin — le
 *  mode d'alerte n'apparaît qu'une fois une date posée, la date d'alerte qu'au choix « à une date
 *  précise » (D4). D5 : retirer l'échéance retire l'alerte, imposé ici avant l'envoi. */
function DueDateDialog({ task, onSave, onClose }: { task: TodoTask; onSave: (payload: { dueDate: string | null; alertMode: string | null; alertDate: string | null }) => void; onClose: () => void }) {
    const [dueDate, setDueDate] = React.useState(task.due_date || '');
    const [alertMode, setAlertMode] = React.useState(task.alert_mode || '');
    const [alertDate, setAlertDate] = React.useState(task.alert_date || '');

    const save = () => {
        if (!dueDate) { onSave({ dueDate: null, alertMode: null, alertDate: null }); onClose(); return; }
        onSave({ dueDate, alertMode: alertMode || null, alertDate: alertMode === 'on_date' ? (alertDate || null) : null });
        onClose();
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-xs shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content">Échéance</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-3">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date</label>
                            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-[var(--brand-color)]" />
                        </div>
                        {dueDate && (
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Me prévenir</label>
                                <select value={alertMode} onChange={(e) => setAlertMode(e.target.value)}
                                    className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-[var(--brand-color)]">
                                    {ALERT_MODE_LABELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                                </select>
                            </div>
                        )}
                        {dueDate && alertMode === 'on_date' && (
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date d'alerte</label>
                                <input type="date" value={alertDate} onChange={(e) => setAlertDate(e.target.value)}
                                    className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-[var(--brand-color)]" />
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm font-bold text-content-muted hover:text-content">Annuler</button>
                            <button onClick={save} disabled={dueDate === '' && !task.due_date} className="px-4 py-2 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold disabled:opacity-40">
                                {dueDate ? 'Enregistrer' : 'Retirer l\'échéance'}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

function TaskRow({ task, criticality, criticalityLevels, onToggle, onDelete, onSetCriticality, onSetDueDate, dragDisabled, isDragging, showDropIndicator, onDragStartTask, onDragOverTask, onDropOnTask, onDragEndTask, onMoveWithinList, onMoveToNeighborList }: {
    task: TodoTask; criticality: Criticality | undefined; criticalityLevels: Criticality[];
    onToggle: () => void; onDelete: () => void; onSetCriticality: (id: string | null) => void;
    onSetDueDate: (payload: { dueDate: string | null; alertMode: string | null; alertDate: string | null }) => void;
    dragDisabled: boolean; isDragging: boolean; showDropIndicator: boolean;
    onDragStartTask: () => void; onDragOverTask: (e: React.DragEvent) => void; onDropOnTask: () => void; onDragEndTask: () => void;
    onMoveWithinList: (dir: -1 | 1) => void; onMoveToNeighborList: (dir: -1 | 1) => void;
}) {
    const [showDueDate, setShowDueDate] = React.useState(false);
    const overdue = !task.done && task.due_date && task.due_date < todayISO();
    const alertActive = isAlertActive(task);
    return (
        <div
            onDragOver={dragDisabled ? undefined : (e) => { e.preventDefault(); onDragOverTask(e); }}
            onDrop={dragDisabled ? undefined : (e) => { e.preventDefault(); onDropOnTask(); }}
            onKeyDown={(e) => {
                if (!e.altKey) return;
                if (e.key === 'ArrowUp') { e.preventDefault(); onMoveWithinList(-1); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); onMoveWithinList(1); }
                else if (e.key === 'ArrowLeft') { e.preventDefault(); onMoveToNeighborList(-1); }
                else if (e.key === 'ArrowRight') { e.preventDefault(); onMoveToNeighborList(1); }
            }}
            className={`group flex items-start gap-1 py-2 px-1 rounded-lg hover:bg-surface-soft outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] ${isDragging ? 'opacity-40' : ''} ${showDropIndicator ? 'border-t-2 border-[var(--brand-color)]' : ''}`}
        >
            {/* E1 : poignée à six points, visible en permanence (pas seulement au survol). */}
            {!dragDisabled && (
                <span draggable onDragStart={onDragStartTask} onDragEnd={onDragEndTask}
                    className="mt-1 text-content-muted/60 hover:text-content-muted cursor-grab active:cursor-grabbing shrink-0" title="Glisser pour réordonner">
                    <GripVertical size={13} />
                </span>
            )}
            <button onClick={onToggle} className="mt-0.5 text-content-muted hover:text-[var(--brand-color)] shrink-0">
                {task.done ? <CheckCircle2 size={18} className="text-emerald-500" /> : <Circle size={18} />}
            </button>
            <div tabIndex={0} className="min-w-0 flex-1">
                <p className={`text-sm ${task.done ? 'line-through text-content-muted' : 'text-content'}`}>{task.name || 'Tâche sans libellé'}</p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {/* D7 : la date reste toujours visible dès qu'elle existe ; en gras + liseré rouge
                        quand l'alerte est active, en rouge quand l'échéance est dépassée. */}
                    <button onClick={() => setShowDueDate(true)}
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${overdue ? 'text-red-500' : alertActive ? 'text-content border-l-2 border-red-500 pl-1' : 'text-content-muted'} hover:bg-surface-soft`}
                        title="Régler l'échéance">
                        {task.due_date ? fmtDate(task.due_date) : '+ Échéance'}
                    </button>
                    <CriticalityPicker value={task.criticality_id} levels={criticalityLevels} onChange={onSetCriticality} />
                </div>
            </div>
            {showDueDate && <DueDateDialog task={task} onSave={onSetDueDate} onClose={() => setShowDueDate(false)} />}
            <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 text-content-muted hover:text-red-500 shrink-0">
                <Trash2 size={14} />
            </button>
        </div>
    );
}

function ListCard({ list, tasks, alertCount, criticalityById, criticalityLevels, otherSpaces, compact, fullWidth, onAddTask, onToggleTask, onDeleteTask, onDeleteList, onShareList, onMoveList, onSetTaskCriticality, onSetTaskDueDate, onRenameList, onRecolorList, dragDisabled, draggingTaskId, dragOverInfo, onDragStartTask, onDragOverSlot, onDropTask, onDragEndTask, onMoveTask, neighborListId, sort, onChangeSort }: {
    list: TodoList; tasks: TodoTask[]; alertCount: number; criticalityById: Map<string, Criticality>; criticalityLevels: Criticality[];
    /** H15 : tableaux candidats pour "déplacer vers" — déjà filtrés (jamais celui où la liste est déjà). */
    otherSpaces: TodoSpace[]; compact?: boolean; fullWidth?: boolean;
    onAddTask: (listId: string, name: string, criticalityId: string | null) => void;
    onToggleTask: (task: TodoTask) => void;
    onDeleteTask: (id: string) => void;
    onDeleteList: (id: string) => void;
    onShareList: (list: TodoList) => void;
    onMoveList: (listId: string, spaceId: string) => void;
    onSetTaskCriticality: (task: TodoTask, criticalityId: string | null) => void;
    onSetTaskDueDate: (task: TodoTask, payload: { dueDate: string | null; alertMode: string | null; alertDate: string | null }) => void;
    onRenameList: (id: string, name: string) => void;
    onRecolorList: (id: string, color: string | null) => void;
    // E1-E7 : réordonnancement par glisser-déposer et clavier.
    dragDisabled: boolean;
    draggingTaskId: string | null;
    dragOverInfo: { listId: string; index: number } | null;
    onDragStartTask: (task: TodoTask, sourceListId: string) => void;
    onDragOverSlot: (listId: string, index: number) => void;
    onDropTask: () => void;
    onDragEndTask: () => void;
    onMoveTask: (task: TodoTask, targetListId: string, targetIndex: number) => void;
    /** E3 : "vers la liste voisine au même rang" — la liste juste à côté dans l'ordre affiché. */
    neighborListId: { prev: string | null; next: string | null };
    // F1-F5 : tri à la demande, propre à cette liste.
    sort: SortState;
    onChangeSort: (sort: SortState) => void;
}) {
    const [draft, setDraft] = React.useState('');
    const [draftCriticality, setDraftCriticality] = React.useState<string | null>(null);
    const [editingTitle, setEditingTitle] = React.useState(false);
    const [titleDraft, setTitleDraft] = React.useState(list.name);
    const [showColorMenu, setShowColorMenu] = React.useState(false);
    const [showSortMenu, setShowSortMenu] = React.useState(false);
    const [showMoveMenu, setShowMoveMenu] = React.useState(false);
    // F1 : recliquer le même critère inverse le sens ; un troisième clic rend l'ordre manuel.
    const cycleSort = (by: SortBy) => {
        if (!sort || sort.by !== by) onChangeSort({ by, dir: 'asc' });
        else if (sort.dir === 'asc') onChangeSort({ by, dir: 'desc' });
        else onChangeSort(null);
    };
    const submit = () => {
        const name = draft.trim();
        if (!name) return;
        onAddTask(list.id, name, draftCriticality);
        setDraft('');
        setDraftCriticality(null);
    };
    const submitTitle = () => {
        const name = titleDraft.trim();
        setEditingTitle(false);
        if (!name || name === list.name) { setTitleDraft(list.name); return; }
        onRenameList(list.id, name);
    };
    const width = fullWidth ? 'w-full' : compact ? 'min-w-[200px] w-[200px]' : 'min-w-[260px] w-[260px]';
    return (
        <div className={`border border-border rounded-2xl p-4 flex flex-col gap-1 shrink-0 ${width} ${list.color ? '' : 'bg-surface'}`}
            style={list.color ? { backgroundColor: list.color } : undefined}>
            <div className="flex items-center justify-between mb-1 gap-1">
                {editingTitle ? (
                    <input autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)}
                        onBlur={submitTitle}
                        onKeyDown={(e) => { if (e.key === 'Enter') submitTitle(); if (e.key === 'Escape') { setTitleDraft(list.name); setEditingTitle(false); } }}
                        className="font-bold text-content text-sm bg-transparent outline-none border-b border-[var(--brand-color)] flex-1 min-w-0" />
                ) : (
                    <p onClick={() => { setTitleDraft(list.name); setEditingTitle(true); }}
                        className="font-bold text-content text-sm truncate cursor-text flex-1 min-w-0" title="Cliquer pour renommer">
                        {list.name} <span className="font-normal text-content-muted">({tasks.length})</span>
                        {alertCount > 0 && (
                            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-black">{alertCount}</span>
                        )}
                    </p>
                )}
                <div className="flex items-center gap-1 shrink-0 relative">
                    <button onClick={() => setShowSortMenu((v) => !v)} className={`hover:text-content ${sort ? 'text-[var(--brand-color)]' : 'text-content-muted'}`} title="Trier">
                        <ArrowUpDown size={13} />
                    </button>
                    {showSortMenu && (
                        <div className="absolute right-0 top-6 z-20 bg-surface border border-border rounded-xl p-1.5 shadow-xl w-40">
                            {SORT_LABELS.map((s) => (
                                <button key={s.by} onClick={() => cycleSort(s.by)}
                                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-left ${sort?.by === s.by ? 'bg-[var(--brand-color)] text-white font-bold' : 'text-content hover:bg-surface-soft'}`}>
                                    {s.label}
                                    {sort?.by === s.by && (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
                                </button>
                            ))}
                        </div>
                    )}
                    <button onClick={() => setShowColorMenu((v) => !v)} className="text-content-muted hover:text-content" title="Couleur de la liste">•••</button>
                    {showColorMenu && (
                        <div className="absolute right-0 top-6 z-20 bg-surface border border-border rounded-xl p-2 shadow-xl flex gap-1.5">
                            {LIST_TINTS.map((t) => (
                                <button key={t.name} onClick={() => { onRecolorList(list.id, t.value); setShowColorMenu(false); }}
                                    title={t.name}
                                    className="w-5 h-5 rounded-full border border-border flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: t.value || 'transparent' }}>
                                    {!t.value && <X size={10} className="text-content-muted" />}
                                </button>
                            ))}
                        </div>
                    )}
                    {/* H4 : ouvert à tous ceux qui voient la liste (savoir qui d'autre y a accès) —
                        seul le dialogue lui-même réserve l'ajout/le retrait au propriétaire. */}
                    <button onClick={() => onShareList(list)} className="text-content-muted hover:text-content" title="Partager cette liste"><Users size={13} /></button>
                    {otherSpaces.length > 0 && (
                        <button onClick={() => setShowMoveMenu((v) => !v)} className="text-content-muted hover:text-content" title="Déplacer vers un autre tableau">
                            <ArrowRightLeft size={13} />
                        </button>
                    )}
                    {showMoveMenu && (
                        // H15 : mes tableaux + ceux qu'on m'a partagés, jamais celui où la liste est déjà
                        // (déjà exclu par le filtre `otherSpaces`) — elle se pose en fin de rang à l'arrivée.
                        <div className="absolute right-0 top-6 z-20 bg-surface border border-border rounded-xl p-1.5 shadow-xl w-48 max-h-56 overflow-auto">
                            {otherSpaces.map((s) => (
                                <button key={s.id} onClick={() => { onMoveList(list.id, s.id); setShowMoveMenu(false); }}
                                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-left text-content hover:bg-surface-soft truncate">
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: colorForSpaceId(s.id) }} />
                                    <span className="truncate">{s.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <button onClick={() => onDeleteList(list.id)} className="text-content-muted hover:text-red-500"><Trash2 size={13} /></button>
                </div>
            </div>
            {sort && (
                // F4 : une liste triée ne se réordonne plus (poignées retirées, clavier coupé, aucun
                // dépôt accepté) — une ligne le dit et propose de revenir à l'ordre manuel.
                <p className="text-[10px] text-content-muted italic -mt-0.5 mb-0.5">
                    Triée par {SORT_LABELS.find((s) => s.by === sort.by)?.label.toLowerCase()} — <button onClick={() => onChangeSort(null)} className="font-bold text-[var(--brand-color)] hover:underline">revenir à mon ordre</button>
                </p>
            )}
            {!sort && dragDisabled && draggingTaskId === null && tasks.length > 1 && (
                // E5 : impossible pendant une recherche/filtre — pas la peine côté clic, mais on l'explique
                // plutôt que de laisser deviner pourquoi la poignée a disparu.
                <p className="text-[10px] text-content-muted italic -mt-0.5 mb-0.5">Réordonner : réinitialisez la recherche/les filtres d'abord.</p>
            )}
            <div className="flex flex-col">
                {(() => {
                    // Le serveur (insertAt) calcule la position d'arrivée sur la liste PRIVÉE de la tâche
                    // qu'on déplace — si elle est déjà dans cette liste, son propre rang ne doit pas compter.
                    // `slot` = index d'insertion dans cette liste-sans-la-tâche-déplacée, cohérent avec le
                    // serveur ; distinct de `i`, le rang d'affichage (qui, lui, l'inclut encore, en grisé).
                    let slot = 0;
                    return tasks.map((t, i) => {
                        const isDraggedRow = draggingTaskId === t.id;
                        const slotBefore = slot;
                        if (!isDraggedRow) slot++;
                        return (
                            <TaskRow key={t.id} task={t} criticality={t.criticality_id ? criticalityById.get(t.criticality_id) : undefined}
                                criticalityLevels={criticalityLevels}
                                onToggle={() => onToggleTask(t)} onDelete={() => onDeleteTask(t.id)}
                                onSetCriticality={(id) => onSetTaskCriticality(t, id)}
                                onSetDueDate={(payload) => onSetTaskDueDate(t, payload)}
                                dragDisabled={dragDisabled}
                                isDragging={isDraggedRow}
                                showDropIndicator={!!draggingTaskId && !isDraggedRow && dragOverInfo?.listId === list.id && dragOverInfo?.index === slotBefore}
                                onDragStartTask={() => onDragStartTask(t, list.id)}
                                onDragOverTask={(e) => {
                                    if (isDraggedRow) return;
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const before = e.clientY < rect.top + rect.height / 2;
                                    onDragOverSlot(list.id, before ? slotBefore : slotBefore + 1);
                                }}
                                onDropOnTask={onDropTask}
                                onDragEndTask={onDragEndTask}
                                onMoveWithinList={(dir) => {
                                    const target = i + dir;
                                    if (target < 0 || target >= tasks.length) return;
                                    onMoveTask(t, list.id, slotBefore + dir);
                                }}
                                onMoveToNeighborList={(dir) => {
                                    const targetListId = dir === -1 ? neighborListId.prev : neighborListId.next;
                                    if (!targetListId) return;
                                    onMoveTask(t, targetListId, slotBefore);
                                }}
                            />
                        );
                    });
                })()}
                {!dragDisabled && draggingTaskId && (
                    <div
                        onDragOver={(e) => { e.preventDefault(); onDragOverSlot(list.id, tasks.filter((t) => t.id !== draggingTaskId).length); }}
                        onDrop={(e) => { e.preventDefault(); onDropTask(); }}
                        className={`h-3 rounded ${dragOverInfo?.listId === list.id && dragOverInfo?.index === tasks.filter((t) => t.id !== draggingTaskId).length ? 'border-t-2 border-[var(--brand-color)]' : ''}`}
                    />
                )}
            </div>
            <div className="mt-2 pt-2 border-t border-border flex items-center gap-1.5">
                <CriticalityPicker value={draftCriticality} levels={criticalityLevels} onChange={setDraftCriticality} />
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                    placeholder="+ Ajouter une tâche"
                    className="flex-1 min-w-0 bg-transparent text-sm text-content placeholder:text-content-muted outline-none"
                />
            </div>
        </div>
    );
}

/**
 * Toute la logique (données + mutations) du ToDo, indépendante de l'habillage — utilisée
 * aussi bien par l'écran plein que par le widget flottant, chacun avec sa propre instance
 * (pas d'état partagé entre les deux : chacun recharge depuis l'API, pas de temps réel).
 */
export function useTodoBoard() {
    const [spaces, setSpaces] = React.useState<TodoSpace[]>([]);
    const [criticality, setCriticality] = React.useState<Criticality[]>([]);
    const [activeSpaceId, setActiveSpaceId] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [query, setQuery] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState<TodoStatusFilter>('tout');
    const [criticalityFilter, setCriticalityFilter] = React.useState<string>('tout');
    const [alertOnlyFilter, setAlertOnlyFilter] = React.useState(false);
    const [showCriticalitySettings, setShowCriticalitySettings] = React.useState(false);
    const [viewMode, setViewMode] = React.useState<'grille' | 'liste'>(() => (localStorage.getItem(TODO_VIEW_MODE_KEY) === 'liste' ? 'liste' : 'grille'));
    const changeViewMode = (mode: 'grille' | 'liste') => { setViewMode(mode); localStorage.setItem(TODO_VIEW_MODE_KEY, mode); };
    const [sharingSpace, setSharingSpace] = React.useState<TodoSpace | null>(null);
    const [sharingList, setSharingList] = React.useState<TodoList | null>(null);
    const [creatingSpace, setCreatingSpace] = React.useState(false);
    const [creatingList, setCreatingList] = React.useState(false);

    // A3 : annulation par Ctrl+Z sur instantanés -- toasts locaux + historique restaurable.
    const [toasts, setToasts] = React.useState<Toast[]>([]);
    const pushToast = React.useCallback((message: string, actionLabel?: string, onAction?: () => void) => {
        const toastId = generateClientId();
        setToasts((prev) => [...prev, { id: toastId, message, actionLabel, onAction }]);
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== toastId)), 8000);
    }, []);
    const dismissToast = (toastId: string) => setToasts((prev) => prev.filter((t) => t.id !== toastId));
    const [showHistory, setShowHistory] = React.useState(false);
    const [history, setHistory] = React.useState<ActionLogEntry[]>([]);
    const [loadingHistory, setLoadingHistory] = React.useState(false);
    const loadHistory = React.useCallback(() => {
        setLoadingHistory(true);
        return todoApi.getActions().then((r: any) => setHistory(r.actions || [])).finally(() => setLoadingHistory(false));
    }, []);

    const loadSpaces = React.useCallback(() => {
        return todoApi.getSpaces().then((r: any) => {
            const s: TodoSpace[] = r.spaces || [];
            setSpaces(s);
            setActiveSpaceId((prev) => (prev && s.some((x) => x.id === prev)) ? prev : (s[0]?.id ?? null));
            return s;
        });
    }, []);

    const loadCriticality = React.useCallback(() => {
        return todoApi.getCriticality().then((r: any) => { setCriticality(r.levels || []); return r.levels || []; });
    }, []);

    React.useEffect(() => {
        Promise.all([loadSpaces(), loadCriticality()]).finally(() => setLoading(false));
    }, [loadSpaces, loadCriticality]);

    // Les listes/tâches d'un tableau ne sont chargées qu'à son ouverture (GET /todo/spaces/:id),
    // et mises en cache par tableau pour ne pas les redemander en changeant d'onglet puis en
    // revenant. Pas de temps réel : un tableau modifié ailleurs ne se met à jour qu'en le rouvrant.
    const [spaceLists, setSpaceLists] = React.useState<Record<string, TodoList[]>>({});
    const [spaceTasks, setSpaceTasks] = React.useState<Record<string, TodoTask[]>>({});
    const [loadedSpaces, setLoadedSpaces] = React.useState<Record<string, boolean>>({});

    const openSpace = React.useCallback((id: string) => {
        setActiveSpaceId(id);
        if (loadedSpaces[id]) return;
        todoApi.getSpaceContent(id).then((r: any) => {
            setSpaceLists((prev) => ({ ...prev, [id]: r.lists || [] }));
            setSpaceTasks((prev) => ({ ...prev, [id]: r.tasks || [] }));
            setLoadedSpaces((prev) => ({ ...prev, [id]: true }));
        });
    }, [loadedSpaces]);

    React.useEffect(() => {
        if (activeSpaceId && !loadedSpaces[activeSpaceId]) openSpace(activeSpaceId);
    }, [activeSpaceId, loadedSpaces, openSpace]);

    // Le widget flottant (FloatingTodoWidget) monte sa PROPRE instance de useTodoBoard, indépendante de
    // celle de l'écran ToDo — créer un tableau depuis l'écran ne le fait donc jamais apparaître dans le
    // widget (resté sur son état chargé une seule fois, au montage global de l'app). Plutôt qu'une
    // synchronisation temps réel entre les deux, le widget rappelle `refreshAll` à chaque ouverture (même
    // philosophie que le reste du module : "pas de temps réel, à jour dès qu'on rouvre").
    const refreshAll = React.useCallback(() => {
        setLoadedSpaces({});
        return loadSpaces();
    }, [loadSpaces]);

    const criticalityById = React.useMemo(() => new Map(criticality.map((c) => [c.id, c])), [criticality]);
    // C7 : « la plus forte d'abord » suit l'ordre des niveaux tel qu'il est rangé dans les
    // réglages (leur position), pas une échelle figée dans le code.
    const criticalityOrder = React.useMemo(() => new Map(criticality.map((c, i) => [c.id, i])), [criticality]);
    const [listSort, setListSortState] = React.useState<Record<string, SortState>>({});
    const getListSort = (listId: string): SortState => listId in listSort ? listSort[listId] : readListSort(listId);
    const setListSort = (listId: string, sort: SortState) => {
        setListSortState((prev) => ({ ...prev, [listId]: sort }));
        if (sort) localStorage.setItem(TODO_SORT_KEY_PREFIX + listId, JSON.stringify(sort));
        else localStorage.removeItem(TODO_SORT_KEY_PREFIX + listId);
    };
    const activeLists = activeSpaceId ? (spaceLists[activeSpaceId] || []) : [];
    const activeTasks = activeSpaceId ? (spaceTasks[activeSpaceId] || []) : [];

    const handleCreateSpace = async (name: string) => {
        const r: any = await todoApi.createSpace(name);
        await loadSpaces();
        setActiveSpaceId(r.space.id);
    };
    const handleDeleteSpace = async (id: string) => {
        if (!window.confirm('Supprimer ce tableau, avec toutes ses listes et tâches ?')) return;
        const space = spaces.find((s) => s.id === id);
        const r: any = await todoApi.deleteSpace(id);
        await loadSpaces();
        if (r.actionId) pushToast(`Tableau « ${space?.name || ''} » supprimé`, 'Annuler', () => handleUndoAction(r.actionId));
    };
    const handleRenameSpace = async (id: string, name: string) => {
        await todoApi.renameSpace(id, name);
        setSpaces((prev) => prev.map((s) => s.id === id ? { ...s, name } : s));
    };
    const handleCreateList = async (name: string) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.createList(activeSpaceId, name);
        setSpaceLists((prev) => ({ ...prev, [activeSpaceId]: [...(prev[activeSpaceId] || []), r.list] }));
    };
    const handleRenameList = async (id: string, name: string) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.updateList(id, { name });
        setSpaceLists((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).map((l) => l.id === id ? r.list : l) }));
    };
    const handleRecolorList = async (id: string, color: string | null) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.updateList(id, { color });
        setSpaceLists((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).map((l) => l.id === id ? r.list : l) }));
    };
    const handleDeleteList = async (id: string) => {
        if (!activeSpaceId) return;
        if (!window.confirm('Supprimer cette liste et ses tâches ?')) return;
        const list = (spaceLists[activeSpaceId] || []).find((l) => l.id === id);
        const r: any = await todoApi.deleteList(id);
        setSpaceLists((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).filter((l) => l.id !== id) }));
        setSpaceTasks((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).filter((t) => t.list_id !== id) }));
        if (r.actionId) pushToast(`Liste « ${list?.name || ''} » supprimée`, 'Annuler', () => handleUndoAction(r.actionId));
    };
    // H15 : déplacer une liste vers un autre tableau — elle se pose en fin de rang à l'arrivée
    // (géré côté serveur). Si le tableau cible n'a encore jamais été ouvert, inutile de forger
    // son cache local : `openSpace` le chargera frais, liste déplacée comprise.
    const handleMoveList = async (listId: string, targetSpaceId: string) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.updateList(listId, { spaceId: targetSpaceId });
        const movedList: TodoList = r.list;
        setSpaceLists((prev) => {
            const next = { ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).filter((l) => l.id !== listId) };
            if (loadedSpaces[targetSpaceId]) next[targetSpaceId] = [...(prev[targetSpaceId] || []), movedList];
            return next;
        });
        setSpaceTasks((prev) => {
            const movedTasks = (prev[activeSpaceId] || []).filter((t) => t.list_id === listId);
            const next = { ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).filter((t) => t.list_id !== listId) };
            if (loadedSpaces[targetSpaceId]) next[targetSpaceId] = [...(prev[targetSpaceId] || []), ...movedTasks];
            return next;
        });
    };
    const handleAddTask = async (listId: string, name: string, criticalityId: string | null = null) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.createTask({ listId, name, criticalityId });
        setSpaceTasks((prev) => ({ ...prev, [activeSpaceId]: [...(prev[activeSpaceId] || []), r.task] }));
    };
    const handleToggleTask = async (task: TodoTask) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.updateTask(task.id, { done: !task.done });
        setSpaceTasks((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).map((t) => t.id === task.id ? r.task : t) }));
    };
    const handleSetTaskCriticality = async (task: TodoTask, criticalityId: string | null) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.updateTask(task.id, { criticalityId });
        setSpaceTasks((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).map((t) => t.id === task.id ? r.task : t) }));
    };
    const handleSetTaskDueDate = async (task: TodoTask, payload: { dueDate: string | null; alertMode: string | null; alertDate: string | null }) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.updateTask(task.id, payload);
        setSpaceTasks((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).map((t) => t.id === task.id ? r.task : t) }));
    };
    // E2/E3 : réordonner (même liste ou vers une autre) — le serveur renvoie les tâches à jour des
    // DEUX listes concernées (positions décalées des voisines), pas seulement celle déplacée.
    const handleMoveTask = async (task: TodoTask, targetListId: string, targetIndex: number) => {
        if (!activeSpaceId) return;
        const r: any = await todoApi.moveTask(task.id, targetListId, targetIndex);
        const updated: TodoTask[] = r.tasks || [];
        const updatedIds = new Set(updated.map((t) => t.id));
        setSpaceTasks((prev) => ({
            ...prev,
            [activeSpaceId]: [...(prev[activeSpaceId] || []).filter((t) => !updatedIds.has(t.id)), ...updated],
        }));
    };
    const handleDeleteTask = async (id: string) => {
        if (!activeSpaceId) return;
        const task = (spaceTasks[activeSpaceId] || []).find((t) => t.id === id);
        const r: any = await todoApi.deleteTask(id);
        setSpaceTasks((prev) => ({ ...prev, [activeSpaceId]: (prev[activeSpaceId] || []).filter((t) => t.id !== id) }));
        if (r.actionId) pushToast(`Tâche « ${task?.name || 'sans nom'} » supprimée`, 'Annuler', () => handleUndoAction(r.actionId));
    };

    // A3 : après une annulation, on rafraîchit le contenu du tableau concerné plutôt que de
    // reconstruire l'état localement -- l'action est rare, un aller-retour serveur reste largement
    // assez rapide et bien plus sûr qu'essayer de deviner l'état exact d'une tâche/liste restaurée.
    const applyUndoResult = async (spaceId: string) => {
        // `spaces` doit déjà contenir le tableau restauré avant qu'on le rende actif -- sinon
        // `TodoBoardContent` cherche un `activeSpaceId` absent de `spaces` le temps d'un rendu.
        const r: any = await todoApi.getSpaceContent(spaceId);
        setSpaceLists((prev) => ({ ...prev, [spaceId]: r.lists || [] }));
        setSpaceTasks((prev) => ({ ...prev, [spaceId]: r.tasks || [] }));
        setLoadedSpaces((prev) => ({ ...prev, [spaceId]: true }));
        await loadSpaces();
        setActiveSpaceId(spaceId);
    };
    const handleUndoAction = async (actionId: string) => {
        const r: any = await todoApi.undoAction(actionId);
        await applyUndoResult(r.action.space_id);
        loadHistory();
    };
    // Ctrl+Z : annule le dernier geste non annulé de l'appelant, quel que soit le tableau où il
    // a eu lieu -- bascule dessus si ce n'est pas déjà celui ouvert.
    const handleUndoLast = async () => {
        const r: any = await todoApi.undoLast().catch(() => null);
        if (!r) return;
        await applyUndoResult(r.action.space_id);
        loadHistory();
        pushToast(`Dernière action annulée : « ${r.action.label} ».`);
    };

    // G2 : la recherche porte sur le nom de la tâche ET celui de sa liste (une liste qui matche
    // montre toutes ses tâches) — accents/casse indifférents (`normalize`). G4 : filtre
    // d'avancement. C6 : filtre de criticité. D10 : filtre "en alerte" (ruban rouge). Se cumulent.
    const filteredTasks = (listId: string) => {
        const list = activeLists.find((l) => l.id === listId);
        const all = activeTasks.filter((t) => t.list_id === listId);
        const q = normalize(query.trim());
        const byQuery = !q ? all : (normalize(list?.name || '').includes(q)
            ? all
            : all.filter((t) => normalize(t.name || '').includes(q)));
        const byStatus = statusFilter === 'tout' ? byQuery : byQuery.filter((t) => (statusFilter === 'faites') === !!t.done);
        // C6 : « jamais une tâche sans niveau » — le filtre ne retient que les tâches qui portent
        // vraiment le niveau choisi, pas celles à « — ».
        const byCriticality = criticalityFilter === 'tout' ? byStatus : byStatus.filter((t) => t.criticality_id === criticalityFilter);
        const byAlert = alertOnlyFilter ? byCriticality.filter(isAlertActive) : byCriticality;
        // F2 : le tri ne range que l'affichage — jamais écrit en base, l'ordre manuel (position)
        // reste intact dessous.
        return sortTasks(byAlert, getListSort(listId), criticalityOrder);
    };

    // G5 : compteur "x / y tâches" sur tout le tableau ouvert (toutes listes confondues).
    const taskCounts = React.useMemo(() => {
        const shown = activeLists.reduce((n, l) => n + filteredTasks(l.id).length, 0);
        return { shown, total: activeTasks.length };
    }, [activeLists, activeTasks, query, statusFilter, criticalityFilter, alertOnlyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // D9/D11 : pastilles chiffrées (liste, tableau) — toujours calculées sur les tâches réellement
    // en alerte, indépendamment des filtres actifs à l'écran.
    const alertCountByList = React.useMemo(() => {
        const map = new Map<string, number>();
        activeTasks.forEach((t) => { if (isAlertActive(t)) map.set(t.list_id, (map.get(t.list_id) || 0) + 1); });
        return map;
    }, [activeTasks]);
    const spaceAlertCounts = React.useMemo(() => {
        const map = new Map<string, number>();
        Object.entries(spaceTasks).forEach(([spaceId, tasks]) => map.set(spaceId, tasks.filter(isAlertActive).length));
        return map;
    }, [spaceTasks]);
    const activeSpaceAlertCount = activeSpaceId ? (spaceAlertCounts.get(activeSpaceId) || 0) : 0;

    const resetFilters = () => { setQuery(''); setStatusFilter('tout'); setCriticalityFilter('tout'); setAlertOnlyFilter(false); };

    const handleCreateCriticality = async (name: string, color: string) => { await todoApi.createCriticality(name, color); loadCriticality(); };
    const handleUpdateCriticality = async (id: string, payload: { name?: string; color?: string }) => { await todoApi.updateCriticality(id, payload); loadCriticality(); };
    const handleDeleteCriticality = async (id: string) => {
        if (!window.confirm('Supprimer ce niveau ? Les tâches qui le portent reviennent à « — ».')) return;
        await todoApi.deleteCriticality(id);
        if (criticalityFilter === id) setCriticalityFilter('tout');
        loadCriticality();
        if (activeSpaceId) openSpace(activeSpaceId); // les tâches affichées gardaient l'ancien niveau en cache
    };
    const handleReorderCriticality = async (order: string[]) => { setCriticality((prev) => order.map((id) => prev.find((l) => l.id === id)!).filter(Boolean)); await todoApi.reorderCriticality(order); };

    return {
        spaces, activeSpaceId, activeLists, criticality, criticalityById, loading, refreshAll, query, setQuery,
        statusFilter, setStatusFilter, criticalityFilter, setCriticalityFilter, alertOnlyFilter, setAlertOnlyFilter,
        viewMode, changeViewMode, taskCounts, resetFilters, alertCountByList, spaceAlertCounts, activeSpaceAlertCount,
        showCriticalitySettings, setShowCriticalitySettings, handleCreateCriticality, handleUpdateCriticality, handleDeleteCriticality, handleReorderCriticality,
        sharingSpace, setSharingSpace, sharingList, setSharingList, creatingSpace, setCreatingSpace, creatingList, setCreatingList,
        openSpace, handleCreateSpace, handleDeleteSpace, handleRenameSpace, handleCreateList, handleRenameList, handleRecolorList,
        handleDeleteList, handleMoveList, handleAddTask, handleToggleTask, handleDeleteTask, handleSetTaskCriticality, handleSetTaskDueDate, handleMoveTask, filteredTasks,
        getListSort, setListSort,
        toasts, dismissToast, showHistory, setShowHistory, history, loadingHistory, loadHistory, handleUndoAction, handleUndoLast,
    };
}

export type TodoBoard = ReturnType<typeof useTodoBoard>;

/** Le rendu partagé : onglets de tableaux, listes, tâches, et les dialogues. */
export function TodoBoardContent({ board, compact }: { board: TodoBoard; compact?: boolean }) {
    const {
        spaces, activeSpaceId, activeLists, criticality, criticalityById, loading, query, setQuery,
        statusFilter, setStatusFilter, criticalityFilter, setCriticalityFilter, alertOnlyFilter, setAlertOnlyFilter,
        viewMode, changeViewMode, taskCounts, resetFilters, alertCountByList, spaceAlertCounts, activeSpaceAlertCount,
        showCriticalitySettings, setShowCriticalitySettings, handleCreateCriticality, handleUpdateCriticality, handleDeleteCriticality, handleReorderCriticality,
        sharingSpace, setSharingSpace, sharingList, setSharingList, creatingSpace, setCreatingSpace, creatingList, setCreatingList,
        openSpace, handleCreateSpace, handleDeleteSpace, handleRenameSpace, handleCreateList, handleRenameList, handleRecolorList,
        handleDeleteList, handleMoveList, handleAddTask, handleToggleTask, handleDeleteTask, handleSetTaskCriticality, handleSetTaskDueDate, handleMoveTask, filteredTasks,
        getListSort, setListSort,
        toasts, dismissToast, showHistory, setShowHistory, history, loadingHistory, loadHistory, handleUndoAction, handleUndoLast,
    } = board;
    const [editingSpaceId, setEditingSpaceId] = React.useState<string | null>(null);
    const [spaceTitleDraft, setSpaceTitleDraft] = React.useState('');

    // E1-E7 : réordonnancement — état de glisser-déposer partagé entre toutes les listes du tableau
    // ouvert (un glisser peut traverser plusieurs ListCard). E5 : désactivé pendant une recherche ou
    // un filtre — l'ordre affiché ne correspond alors plus à l'ordre réel, "juste après celle-ci" ne
    // voudrait plus rien dire.
    const [draggingTask, setDraggingTask] = React.useState<TodoTask | null>(null);
    const [dragOverInfo, setDragOverInfo] = React.useState<{ listId: string; index: number } | null>(null);
    const dragDisabled = !!(query.trim() || statusFilter !== 'tout' || criticalityFilter !== 'tout' || alertOnlyFilter);
    const handleDragStartTask = (task: TodoTask, _sourceListId: string) => setDraggingTask(task);
    const handleDragOverSlot = (listId: string, index: number) => setDragOverInfo({ listId, index });
    const handleDragEndTask = () => { setDraggingTask(null); setDragOverInfo(null); };
    const handleDropTask = () => {
        if (draggingTask && dragOverInfo) {
            // Un glisser-déposer dans sa propre liste, sur la case juste après soi, ne bouge rien.
            handleMoveTask(draggingTask, dragOverInfo.listId, dragOverInfo.index);
        }
        handleDragEndTask();
    };

    if (loading) {
        return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-content-muted" size={compact ? 20 : 28} /></div>;
    }

    const STATUS_LABELS: { key: TodoStatusFilter; label: string }[] = [
        { key: 'a_faire', label: 'À faire' }, { key: 'faites', label: 'Faites' }, { key: 'tout', label: 'Tout' },
    ];

    return (
        <div className={compact ? 'space-y-3' : 'space-y-4'}>
            {!compact && (
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une tâche ou une liste…"
                            className="bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-content outline-none w-64" />
                    </div>
                    <div className="flex rounded-xl border border-border overflow-hidden">
                        {STATUS_LABELS.map((s) => (
                            <button key={s.key} onClick={() => setStatusFilter(s.key)}
                                className={`px-3 py-2 text-xs font-bold transition-all ${statusFilter === s.key ? 'bg-[var(--brand-color)] text-white' : 'text-content-muted hover:text-content'}`}>
                                {s.label}
                            </button>
                        ))}
                    </div>
                    <select value={criticalityFilter} onChange={(e) => setCriticalityFilter(e.target.value)}
                        className="bg-surface border border-border rounded-xl px-2.5 py-2 text-xs font-bold text-content outline-none">
                        <option value="tout">Toutes criticités</option>
                        {criticality.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button onClick={() => setShowCriticalitySettings(true)} title="Régler les niveaux de criticité"
                        className="p-2 rounded-xl border border-border text-content-muted hover:text-content">
                        <Settings size={14} />
                    </button>
                    <div className="flex rounded-xl border border-border overflow-hidden">
                        <button onClick={() => changeViewMode('grille')} title="Affichage grille"
                            className={`p-2 transition-all ${viewMode === 'grille' ? 'bg-[var(--brand-color)] text-white' : 'text-content-muted hover:text-content'}`}>
                            <LayoutGrid size={14} />
                        </button>
                        <button onClick={() => changeViewMode('liste')} title="Affichage liste"
                            className={`p-2 transition-all ${viewMode === 'liste' ? 'bg-[var(--brand-color)] text-white' : 'text-content-muted hover:text-content'}`}>
                            <Rows3 size={14} />
                        </button>
                    </div>
                    {activeSpaceId && (
                        <span className="text-xs text-content-muted flex items-center gap-2">
                            {taskCounts.shown} / {taskCounts.total} tâches
                            {(query.trim() || statusFilter !== 'tout' || criticalityFilter !== 'tout' || alertOnlyFilter) && (
                                <button onClick={resetFilters} className="font-bold text-[var(--brand-color)] hover:underline">Réinitialiser</button>
                            )}
                        </span>
                    )}
                </div>
            )}

            {(() => {
                // H9 : « À moi » / « Partagés avec moi » — le second groupe (et son intitulé)
                // n'existe que s'il y a au moins un tableau reçu, jamais une colonne vide.
                const mySpaces = spaces.filter((s) => s.mine);
                const sharedSpaces = spaces.filter((s) => !s.mine);
                const renderPill = (s: TodoSpace) => {
                    const alertCount = spaceAlertCounts.get(s.id) || 0;
                    return (
                        <button key={s.id}
                            onClick={() => openSpace(s.id)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all flex items-center gap-1.5 ${activeSpaceId === s.id ? 'bg-[var(--brand-color)] text-white border-transparent' : 'border-border text-content-muted hover:text-content'}`}>
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: colorForSpaceId(s.id) }} />
                            {s.name}{!s.mine && <Users size={10} />}
                            {/* D9 : pastille chiffrée — seulement pour un tableau déjà ouvert au moins une fois
                                (les listes/tâches ne sont chargées qu'à l'ouverture, pas de compte à l'aveugle). */}
                            {alertCount > 0 && (
                                <span className={`inline-flex items-center justify-center min-w-[14px] h-3.5 px-1 rounded-full text-[9px] font-black ${activeSpaceId === s.id ? 'bg-white/25 text-white' : 'bg-red-500 text-white'}`}>{alertCount}</span>
                            )}
                        </button>
                    );
                };
                return (
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 flex-wrap items-center">
                            {sharedSpaces.length > 0 && <span className="text-[10px] font-bold uppercase text-content-muted mr-0.5">À moi</span>}
                            {mySpaces.map(renderPill)}
                            <button onClick={() => setCreatingSpace(true)} className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-dashed border-border text-content-muted hover:text-content flex items-center gap-1">
                                <Plus size={12} /> Tableau
                            </button>
                        </div>
                        {sharedSpaces.length > 0 && (
                            <div className="flex gap-2 flex-wrap items-center">
                                <span className="text-[10px] font-bold uppercase text-content-muted mr-0.5">Partagés avec moi</span>
                                {sharedSpaces.map(renderPill)}
                            </div>
                        )}
                    </div>
                );
            })()}

            {activeSpaceId && (() => {
                const activeSpace = spaces.find((s) => s.id === activeSpaceId)!;
                return (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            {editingSpaceId === activeSpace.id ? (
                                <input autoFocus value={spaceTitleDraft} onChange={(e) => setSpaceTitleDraft(e.target.value)}
                                    onBlur={() => {
                                        const name = spaceTitleDraft.trim();
                                        setEditingSpaceId(null);
                                        if (name && name !== activeSpace.name) handleRenameSpace(activeSpace.id, name);
                                    }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingSpaceId(null); }}
                                    className="text-sm font-bold text-content bg-transparent outline-none border-b border-[var(--brand-color)]" />
                            ) : (
                                <p onClick={() => { setSpaceTitleDraft(activeSpace.name); setEditingSpaceId(activeSpace.id); }}
                                    className="text-sm font-bold text-content cursor-text" title="Cliquer pour renommer">{activeSpace.name}</p>
                            )}
                            {/* H10 : on travaille dans l'autre moitié de l'écran — écrire chez quelqu'un
                                sans le savoir est exactement ce que cette pilule empêche. */}
                            {!activeSpace.mine && (
                                <span className="text-[10px] font-bold uppercase text-content-muted flex items-center gap-1 px-2 py-1 rounded-full bg-surface-soft">
                                    <Users size={11} /> Partagé avec moi
                                </span>
                            )}
                            {/* H4 : ouvert à tous ceux qui voient le tableau, lecture seule pour les autres. */}
                            <button onClick={() => setSharingSpace(activeSpace)} className="text-[11px] font-bold text-content-muted hover:text-content flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border">
                                <Users size={12} /> Partager
                            </button>
                            {/* A3 : historique des suppressions/déplacements, restaurable même après
                                avoir quitté la page — ouvert à tous ceux qui voient le tableau. */}
                            <button onClick={() => { setShowHistory(true); loadHistory(); }} className="text-[11px] font-bold text-content-muted hover:text-content flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border">
                                <History size={12} /> Historique
                            </button>
                            {activeSpace.mine && (
                                <button onClick={() => handleDeleteSpace(activeSpace.id)} className="text-[11px] font-bold text-content-muted hover:text-red-500 flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border">
                                    <Trash2 size={12} /> Supprimer
                                </button>
                            )}
                        </div>

                        {/* D10 : ruban rouge, filtre les tâches en alerte d'un clic ; un second clic revient à tout. */}
                        {!compact && activeSpaceAlertCount > 0 && (
                            <button onClick={() => setAlertOnlyFilter((v) => !v)}
                                className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${alertOnlyFilter ? 'bg-red-500 text-white' : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'}`}>
                                {activeSpaceAlertCount} tâche{activeSpaceAlertCount > 1 ? 's' : ''} en alerte {alertOnlyFilter ? '— cliquer pour tout revoir' : '— cliquer pour filtrer'}
                            </button>
                        )}

                        <div className={!compact && viewMode === 'liste' ? 'flex flex-col gap-3' : 'flex gap-3 overflow-x-auto pb-2'}>
                            {activeLists.map((l, li) => (
                                <ListCard key={l.id} list={l} tasks={filteredTasks(l.id)} alertCount={alertCountByList.get(l.id) || 0} criticalityById={criticalityById} criticalityLevels={criticality} otherSpaces={spaces.filter((s) => s.id !== l.space_id)} compact={compact}
                                    fullWidth={!compact && viewMode === 'liste'}
                                    onAddTask={handleAddTask} onToggleTask={handleToggleTask} onDeleteTask={handleDeleteTask} onDeleteList={handleDeleteList} onShareList={setSharingList} onMoveList={handleMoveList} onSetTaskCriticality={handleSetTaskCriticality}
                                    onSetTaskDueDate={handleSetTaskDueDate} onRenameList={handleRenameList} onRecolorList={handleRecolorList}
                                    dragDisabled={dragDisabled || !!getListSort(l.id)} draggingTaskId={draggingTask?.id ?? null} dragOverInfo={dragOverInfo}
                                    onDragStartTask={handleDragStartTask} onDragOverSlot={handleDragOverSlot} onDropTask={handleDropTask} onDragEndTask={handleDragEndTask}
                                    onMoveTask={handleMoveTask}
                                    neighborListId={{ prev: li > 0 ? activeLists[li - 1].id : null, next: li < activeLists.length - 1 ? activeLists[li + 1].id : null }}
                                    sort={getListSort(l.id)} onChangeSort={(s) => setListSort(l.id, s)}
                                />
                            ))}
                            <button onClick={() => setCreatingList(true)}
                                className={`shrink-0 h-fit py-3 rounded-2xl border border-dashed border-border text-content-muted hover:text-content text-sm font-bold flex items-center justify-center gap-1.5 ${!compact && viewMode === 'liste' ? 'w-full' : compact ? 'min-w-[200px] w-[200px]' : 'min-w-[260px] w-[260px]'}`}>
                                <Plus size={15} /> Nouvelle liste
                            </button>
                        </div>
                    </div>
                );
            })()}

            {spaces.length === 0 && (
                <div className="text-center py-10 bg-surface rounded-2xl border border-dashed border-border">
                    <p className="text-content-muted text-sm">Aucun tableau pour l'instant.</p>
                    <div className="mt-3 flex items-center justify-center gap-2">
                        <button onClick={() => setCreatingSpace(true)} className="px-4 py-2 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold">
                            Créer mon premier tableau
                        </button>
                        {/* A3 : le seul accès à l'historique restant si le tableau qu'on vient de
                            supprimer était le dernier -- sans lui, aucun moyen de le restaurer. */}
                        <button onClick={() => { setShowHistory(true); loadHistory(); }}
                            className="px-4 py-2 rounded-lg border border-border text-content-muted hover:text-content text-sm font-bold flex items-center gap-1.5">
                            <History size={14} /> Historique
                        </button>
                    </div>
                </div>
            )}

            {sharingSpace && <ShareDialog type="space" id={sharingSpace.id} name={sharingSpace.name} onClose={() => setSharingSpace(null)} />}
            {showCriticalitySettings && (
                <CriticalitySettingsDialog levels={criticality} onCreate={handleCreateCriticality} onUpdate={handleUpdateCriticality}
                    onDelete={handleDeleteCriticality} onReorder={handleReorderCriticality} onClose={() => setShowCriticalitySettings(false)} />
            )}
            {sharingList && <ShareDialog type="list" id={sharingList.id} name={sharingList.name} onClose={() => setSharingList(null)} />}
            {creatingSpace && (
                <PromptDialog title="Nouveau tableau" placeholder="Nom du tableau" confirmLabel="Créer le tableau"
                    onSubmit={handleCreateSpace} onClose={() => setCreatingSpace(false)} />
            )}
            {creatingList && (
                <PromptDialog title="Nouvelle liste" placeholder="Nom de la liste" confirmLabel="Créer la liste"
                    onSubmit={handleCreateList} onClose={() => setCreatingList(false)} />
            )}
            {showHistory && (
                <HistoryDialog actions={history} loading={loadingHistory}
                    onRestore={(actionId) => handleUndoAction(actionId)} onClose={() => setShowHistory(false)} />
            )}
            <ToastStack toasts={toasts} onDismiss={dismissToast} />
        </div>
    );
}

export const TODO_WIDGET_ENABLED_KEY = 'iamani-todo-widget-enabled';
export const TODO_WIDGET_TOGGLE_EVENT = 'iamani-todo-widget-toggle';

function WidgetToggle() {
    const [enabled, setEnabled] = React.useState(() => localStorage.getItem(TODO_WIDGET_ENABLED_KEY) === 'true');
    const toggle = () => {
        const next = !enabled;
        setEnabled(next);
        localStorage.setItem(TODO_WIDGET_ENABLED_KEY, String(next));
        window.dispatchEvent(new CustomEvent(TODO_WIDGET_TOGGLE_EVENT, { detail: next }));
    };
    return (
        <button onClick={toggle}
            className={`text-xs font-bold flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all ${enabled ? 'bg-[var(--brand-color)] text-white border-transparent' : 'border-border text-content-muted hover:text-content'}`}>
            <ListChecks size={14} /> Widget flottant {enabled ? 'activé' : 'désactivé'}
        </button>
    );
}

export const TodoModule: React.FC = () => {
    const board = useTodoBoard();
    // A3 : Ctrl+Z (Cmd+Z sur Mac) annule le dernier geste — posé UNIQUEMENT ici (l'écran plein),
    // pas dans le widget flottant qui réutilise le même TodoBoardContent : les deux peuvent être
    // montés en même temps sur une page, un seul écouteur global évite un double-déclenchement.
    React.useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                board.handleUndoLast();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [board.handleUndoLast]);
    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2"><ListChecks size={22} /> ToDo</h1>
                    <p className="text-content-muted mt-1 text-sm">Vos tableaux de tâches, privés ou partagés avec vos collègues.</p>
                </div>
                <WidgetToggle />
            </header>
            <TodoBoardContent board={board} />
        </div>
    );
};

export default TodoModule;
